import { EntryStatus, EntryWithPredictions } from '@staycool/contests-types/entry';
import { MarketParlay } from '@staycool/contests-types/leg';
import { DATE_YEAR_TIME_FORMAT, getFormattedDate } from './date';
import sumBy from 'lodash/sumBy';
import { formatLine } from './sports/market';

function getTeamNameByOutcomeResultKey(resultKey: string, market: MarketParlay) {
    if (resultKey === '[Home]') {
        return market.home_team_name;
    }

    if (resultKey === '[Away]') {
        return market.away_team_name;
    }
}

function getOutcomeName({
    outcomeName,
    home_team,
    away_team,
}: {
    outcomeName: string;
    home_team?: string;
    away_team?: string;
}) {
    const name = outcomeName;
    const awayTeam = away_team || '';
    const homeTeam = home_team || '';
    return formatOutcomeName(name, awayTeam, homeTeam);
}

function formatOutcomeName(outcomeName: string, away_team_name: string, home_team_name: string) {
    return outcomeName
        .replace('[Home]', home_team_name)
        .replace('[Player 1]', home_team_name)
        .replace('[Away]', away_team_name)
        .replace('[Player 2]', away_team_name);
}

function formatMarketMatchNameForTicket(marketMatchName: string) {
    return marketMatchName.replace('-', '@');
}

function getLineByResultKey(resultKey: string, line: number) {
    if (line === 0) {
        return '';
    }

    if (resultKey === '[Home]') {
        return line > 0 ? `+${line}` : line;
    }

    if (resultKey === '[Away]') {
        return line > 0 ? `-${line}` : `+${-line}`;
    }
}

function getLineByOutcomeName(
    outcomeName: string,
    line?: number | null,
    marketName?: string,
    resultKey?: string,
    shouldShowZeroLine = false,
) {
    const lineInOutcomeRegex = /\d+$/g;
    const isPkMarket = marketName?.includes('Handicap') || marketName?.includes('Spread');

    const keyToCheck = resultKey || outcomeName;

    if (
        line == undefined ||
        (line === 0 && !shouldShowZeroLine && !isPkMarket) ||
        lineInOutcomeRegex.test(outcomeName)
    ) {
        return '';
    }

    if (line === 0) {
        return !isPkMarket ? line : 'PK';
    }

    if (keyToCheck.toLowerCase().includes('over') || keyToCheck.toLowerCase().includes('under')) {
        return line;
    }

    if (line < 0) {
        return keyToCheck === '[Home]' ? formatLine(line) : `+${formatLine(-line)}`;
    }

    if (line > 0) {
        return keyToCheck === '[Home]' ? `+${formatLine(line)}` : formatLine(-line);
    }
}

function getMatchTimeStart(market: MarketParlay) {
    return getFormattedDate({
        date: market.match_start,
        format: DATE_YEAR_TIME_FORMAT,
    });
}

function formatMarketName(marketName: string) {
    return marketName
        .replace('Handicap', 'Spread')
        .replace('1X2', 'Home-Draw-Away')
        .replace('Teaser ', '')
        .replace('Parlay ', '');
}

function getEntriesMetaData(activeLegId: string, entries?: EntryWithPredictions[]) {
    const wonEntry = entries?.filter((entry) =>
        [EntryStatus.WON, EntryStatus.CLAIMED, EntryStatus.PUSHED].includes(entry.status),
    );
    const availableEntryCount =
        entries?.filter((entry) => ![EntryStatus.ELIMINATED, EntryStatus.DROPPED_OUT].includes(entry.status))?.length ||
        0;
    const usedEntryCount =
        entries?.filter((entry) => [EntryStatus.WON, EntryStatus.ONGOING].includes(entry.status))?.length || 0;

    const unUsedEntryCount = entries?.filter((entry) => {
        return (
            entry.status === EntryStatus.ONGOING &&
            (entry?.predictions || []).filter((pre) => pre.leg_id === activeLegId).length === 0
        );
    });

    return {
        wonEntryCount: wonEntry?.length || 0,
        availableEntryCount,
        usedEntryCount,
        userEntryCount: entries?.length || 0,
        wonEntryAmount: Number(sumBy(wonEntry || [], 'win_amount').toFixed(2)),
        unUsedEntryCount: unUsedEntryCount?.length || 0,
    };
}

export {
    getOutcomeName,
    getTeamNameByOutcomeResultKey,
    getLineByOutcomeName,
    getLineByResultKey,
    formatMarketMatchNameForTicket,
    formatMarketName,
    getMatchTimeStart,
    getEntriesMetaData,
};
